import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import api, { API_BASE } from "@/lib/api";

const uid = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `m-${Date.now()}-${Math.random()}`);

export function AskAssistant() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = user ? `assistant-${user.id}` : null;

  useEffect(() => {
    if (open && sessionId && messages.length === 0) {
      api.get(`/assistant/history/${sessionId}`)
        .then(({ data }) => { if (data.length) setMessages(data); })
        .catch((err) => console.error("Assistant history load failed", err));
    }
    // Runs only when the panel opens / session changes; other refs are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sessionId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  if (!user || !(user.premium || user.role === "admin")) return null;

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { id: uid(), role: "user", content: msg }, { id: uid(), role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API_BASE}/assistant/chat`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ session_id: sessionId, message: msg }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const line = p.replace(/^data: /, "").trim();
          if (!line) continue;
          try {
            const obj = JSON.parse(line);
            if (obj.delta) {
              setMessages((m) => {
                const c = [...m];
                const last = c[c.length - 1];
                if (!last || last.role !== "assistant") {
                  return [...c, { id: uid(), role: "assistant", content: obj.delta }];
                }
                c[c.length - 1] = { ...last, content: last.content + obj.delta };
                return c;
              });
            }
          } catch { /* ignore */ }
        }
      }
    } catch {
      setMessages((m) => {
        const c = [...m];
        const last = c[c.length - 1];
        if (!last || last.role !== "assistant") {
          return [...c, { id: uid(), role: "assistant", content: "The mentor is momentarily unavailable." }];
        }
        c[c.length - 1] = { ...last, content: "The mentor is momentarily unavailable." };
        return c;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      {!open && (
        <button data-testid="ask-ai-launcher" onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full bg-persian-rose hover:bg-persian-rosehover text-white pl-4 pr-5 py-3 shadow-xl shadow-persian-rose/30 transition-transform hover:scale-105">
          <MessageCircle className="h-5 w-5" />
          <span className="font-semibold text-sm">Ask AI Anything</span>
        </button>
      )}

      {open && (
        <div data-testid="ask-ai-panel"
          className="fixed bottom-6 right-6 z-50 w-[calc(100vw-3rem)] sm:w-96 h-[520px] max-h-[80vh] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 bg-navy text-white">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-persian-blue" />
              <span className="font-serif text-lg">Ask AI Anything</span>
            </div>
            <button data-testid="ask-ai-close" onClick={() => setOpen(false)} className="text-slate-300 hover:text-white">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-navy-soft/40" data-testid="ask-ai-messages">
            {messages.length === 0 && (
              <div className="h-full flex flex-col items-center justify-center text-center px-4">
                <MessageCircle className="h-9 w-9 text-slate-300 mb-2" />
                <p className="text-sm text-slate-500">Ask your Socratic Mentor anything — a tricky concept, a study plan, a definition, or how to approach a problem.</p>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={m.id || i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user" ? "bg-navy text-white rounded-br-sm" : "bg-white text-navy border border-slate-200 rounded-bl-sm"
                }`}>
                  {m.content || (streaming && i === messages.length - 1 ? <Loader2 className="h-4 w-4 animate-spin text-persian-blue" /> : "")}
                </div>
              </div>
            ))}
          </div>

          <div className="p-3 border-t border-slate-100 bg-white">
            <div className="flex items-end gap-2">
              <Textarea value={input} onChange={(e) => setInput(e.target.value)} rows={1}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Ask anything academic…" data-testid="ask-ai-input"
                className="resize-none min-h-[42px] max-h-28" />
              <Button onClick={() => send()} disabled={streaming || !input.trim()} data-testid="ask-ai-send"
                className="bg-persian-rose hover:bg-persian-rosehover text-white h-10 w-10 p-0 shrink-0">
                {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
